import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Card, CardContent, Typography, Box, Button, Chip, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Menu, MenuItem
} from '@mui/material';
import {
  Refresh, PlayArrow, Stop, Autorenew, Computer as ComputerIcon, CheckCircle, Error as ErrorIcon, Edit as EditIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { GlassmorphicCard } from '../styles/sharedStyles';

// Available VM icons
const VM_ICONS = [
  { id: 'linux', name: 'Linux', path: '/linux.png' },
  { id: 'ubuntu', name: 'Ubuntu', path: '/ubuntu-linux.png' },
  { id: 'windows10', name: 'Windows 10', path: '/windows-10.png' },
  { id: 'windows11', name: 'Windows 11', path: '/windows-11.png' },
];

// LocalStorage key for VM icon assignments
const VM_ICONS_STORAGE_KEY = 'nasmon_vm_icons';

const StyledCard = styled(GlassmorphicCard)(({ theme }) => ({
  height: '100%',
  '&:hover': {
    transform: 'translateY(-5px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.02)',
  borderRadius: '16px',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.06)',
  '& .MuiTableHead-root': {
    '& .MuiTableCell-head': {
      background: 'rgba(255, 255, 255, 0.05)',
      color: '#ffffff',
      fontWeight: 600,
      fontSize: '0.875rem',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    },
  },
  '& .MuiTableBody-root': {
    '& .MuiTableRow-root': {
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.02)',
      },
      '& .MuiTableCell-body': {
        color: '#ffffff',
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        fontSize: '0.875rem',
      },
    },
  },
}));

const StatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'status'
})(({ status }) => {
  let bgColor, textColor, borderColor;

  const statusStr = String(status || 'unknown').toLowerCase();

  switch (statusStr) {
    case 'running':
    case 'up':
      bgColor = 'rgba(0, 255, 136, 0.1)';
      textColor = '#00ff88';
      borderColor = 'rgba(0, 255, 136, 0.3)';
      break;
    case 'stopped':
    case 'down':
      bgColor = 'rgba(255, 68, 68, 0.1)';
      textColor = '#ff4444';
      borderColor = 'rgba(255, 68, 68, 0.3)';
      break;
    case 'starting':
    case 'stopping':
      bgColor = 'rgba(255, 193, 7, 0.1)';
      textColor = '#ffc107';
      borderColor = 'rgba(255, 193, 7, 0.3)';
      break;
    default:
      bgColor = 'rgba(158, 158, 158, 0.1)';
      textColor = '#9e9e9e';
      borderColor = 'rgba(158, 158, 158, 0.3)';
  }

  return {
    background: bgColor,
    color: textColor,
    border: `1px solid ${borderColor}`,
    fontWeight: 500,
  };
});

const ActionButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  borderRadius: '8px',
  fontSize: '0.75rem',
  padding: '4px 8px',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
  },
}));

// Helper to load VM icons from backend
const loadVMIcons = async (token, apiBaseUrl) => {
  try {
    console.log('🔍 Loading VM icons from backend...');
    const response = await axios.get(`${apiBaseUrl}/api/vms/icons`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    console.log('✅ VM icons response:', response.data);
    const iconAssignments = response.data.icon_assignments || [];
    const iconsMap = {};
    iconAssignments.forEach(assignment => {
      console.log(`   Assigning icon ${assignment.icon_id} to VM ${assignment.vm_id} (${typeof assignment.vm_id})`);
      iconsMap[assignment.vm_id] = assignment.icon_id;
    });
    console.log('🔄 Icons map:', iconsMap);
    return iconsMap;
  } catch (error) {
    console.error('❌ Failed to load VM icons:', error);
    return {};
  }
};

// Helper to save VM icon to backend
const saveVMIcon = async (token, apiBaseUrl, vmId, vmName, iconId) => {
  try {
    console.log('🔍 Saving VM icon:', {
      vmId,
      vmIdType: typeof vmId,
      vmName,
      iconId
    });
    const response = await axios.post(`${apiBaseUrl}/api/vms/icons`, {
      vm_id: vmId,
      vm_name: vmName,
      icon_id: iconId
    }, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    console.log('✅ Save VM icon response:', response.data);
  } catch (error) {
    console.error('❌ Failed to save VM icon:', error);
    if (error.response) {
      console.error('   Response data:', error.response.data);
      console.error('   Response status:', error.response.status);
      console.error('   Response headers:', error.response.headers);
    } else if (error.request) {
      console.error('   Request:', error.request);
    } else {
      console.error('   Error message:', error.message);
    }
  }
};

export default function VirtualMachines({ token, apiBaseUrl }) {
  const [vms, setVms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [error, setError] = useState(null);
  const [selectedVM, setSelectedVM] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, vm: null, action: null });
  const [vmIcons, setVmIcons] = useState({});
  const [iconMenuAnchor, setIconMenuAnchor] = useState(null);
  const [iconMenuVM, setIconMenuVM] = useState(null);

  const API_BASE_URL = apiBaseUrl || window.location.origin;

  const fetchVMs = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/vms`, {
        timeout: 15000,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      setVms(response.data.virtual_machines || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching VMs:', err.message);
      setError(`Failed to fetch VMs: ${err.message}`);
      setVms([]);
    } finally {
      setLoading(false);
    }
  }, [token, API_BASE_URL]);

  const fetchVMIcons = useCallback(async () => {
    if (!token) return;
    const icons = await loadVMIcons(token, API_BASE_URL);
    setVmIcons(icons);
  }, [token, API_BASE_URL]);

  useEffect(() => {
    fetchVMs();
    fetchVMIcons();
    // Refresh VMs every 30 seconds
    const interval = setInterval(fetchVMs, 30000);
    return () => clearInterval(interval);
  }, [fetchVMs, fetchVMIcons]);

  const performAction = async (vm, action) => {
    if (!token) return;

    const endpoints = {
      'power-on': '/api/vms/power-on',
      'power-off': '/api/vms/power-off',
      'restart': '/api/vms/restart'
    };

    try {
      setActionLoading(prev => ({ ...prev, [vm.id]: true }));
      const response = await axios.post(`${API_BASE_URL}${endpoints[action]}`, {
        vm_id: vm.id
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        timeout: 15000
      });

      if (response.data.success) {
        // Refresh VMs after action
        setTimeout(() => fetchVMs(), 1000);
      }
    } catch (err) {
      console.error(`Error performing ${action}:`, err.message);
      setError(`Failed to ${action}: ${err.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [vm.id]: false }));
      setConfirmDialog({ open: false, vm: null, action: null });
    }
  };

  const handleActionClick = (vm, action) => {
    setConfirmDialog({ open: true, vm, action });
  };

  const confirmAction = () => {
    if (confirmDialog.vm && confirmDialog.action) {
      performAction(confirmDialog.vm, confirmDialog.action);
    }
  };

  const getActionText = (action) => {
    const texts = {
      'power-on': 'Power On',
      'power-off': 'Power Off',
      'restart': 'Restart'
    };
    return texts[action] || action;
  };

  const getStatusColor = (state) => {
    if (!state) return 'default';
    const lower = state.toLowerCase();
    if (lower.includes('running') || lower.includes('up')) return 'success';
    if (lower.includes('stopped') || lower.includes('down')) return 'error';
    return 'default';
  };
  const getVMStatus = (vm) => {
    // Handle different status property names and types
    let status = vm.status || vm.state || 'Unknown';
    
    // If it's an object, try to extract a string value
    if (typeof status === 'object' && status !== null) {
      status = status.state || status.name || String(status) || 'Unknown';
    }
    
    return String(status).trim();
  };

  const getVMName = (vm) => {
    return String(vm.name || 'Unknown VM').trim();
  };

  const getVMVCPU = (vm) => {
    const vcpus = vm.vcpus || vm.cpu || vm.cores;
    return vcpus ? String(vcpus).trim() : '-';
  };

  const getVMMemory = (vm) => {
    const memory = vm.memory || vm.ram || vm.memory_in_mb;
    return memory ? String(memory).trim() : '-';
  };

  const getVMIcon = (vmId, vmName) => {
    const stringVmId = String(vmId);
    const stringVmName = String(vmName);
    const iconId = vmIcons[stringVmId] || vmIcons[stringVmName];
    if (iconId) {
      return VM_ICONS.find(icon => icon.id === iconId);
    }
    return null;
  };

  const handleIconMenuOpen = (event, vm) => {
    event.stopPropagation();
    setIconMenuAnchor(event.currentTarget);
    setIconMenuVM(vm);
  };

  const handleIconMenuClose = () => {
    setIconMenuAnchor(null);
    setIconMenuVM(null);
  };

  const handleIconSelect = (iconId) => {
    if (iconMenuVM) {
      const vmKey = String(iconMenuVM.id || iconMenuVM.name);
      const newIcons = { ...vmIcons, [vmKey]: iconId };
      setVmIcons(newIcons);
      saveVMIcon(token, API_BASE_URL, vmKey, iconMenuVM.name, iconId);
    }
    handleIconMenuClose();
  };

  const handleIconClear = () => {
    if (iconMenuVM) {
      const vmKey = String(iconMenuVM.id || iconMenuVM.name);
      const newIcons = { ...vmIcons };
      delete newIcons[vmKey];
      setVmIcons(newIcons);
      saveVMIcon(token, API_BASE_URL, vmKey, iconMenuVM.name, null);
    }
    handleIconMenuClose();
  };

  if (!token) {
    return (
      <StyledCard>
        <CardContent>
          <Typography color="textSecondary">Please log in to view VMs</Typography>
        </CardContent>
      </StyledCard>
    );
  }

  return (
    <StyledCard>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={{ xs: 2, sm: 3 }} flexWrap="wrap" gap={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <ComputerIcon sx={{ color: '#ffd700', fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
            <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Virtual Machines
            </Typography>
          </Box>
          <Tooltip title="Refresh VMs">
            <IconButton
              onClick={fetchVMs}
              disabled={loading}
              size="small"
              sx={{ color: '#ffd700' }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        )}

        {!loading && vms.length === 0 && (
          <Typography sx={{ color: '#999', textAlign: 'center', py: 3, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            No virtual machines found or unable to connect to TrueNAS
          </Typography>
        )}

        {!loading && vms.length > 0 && (
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <StyledTableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">vCPU</TableCell>
                    <TableCell align="center">Memory (MB)</TableCell>
                    <TableCell align="center">Actions</TableCell>
                    <TableCell align="center">Icon</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vms.map((vm) => {
                    const vmStatus = getVMStatus(vm);
                    const vmName = getVMName(vm);
                    const vmVCPU = getVMVCPU(vm);
                    const vmMemory = getVMMemory(vm);
                    const vmIcon = getVMIcon(vm.id, vm.name);
                    
                    return (
                      <TableRow key={vm.id || vm.name}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {vmIcon ? (
                              <Box
                                component="img"
                                src={vmIcon.path}
                                alt={vmIcon.name}
                                sx={{ width: 24, height: 24, objectFit: 'contain' }}
                              />
                            ) : (
                              <ComputerIcon sx={{ fontSize: 24, color: 'rgba(255, 255, 255, 0.5)' }} />
                            )}
                            <Typography>{vmName}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <StatusChip
                            label={vmStatus}
                            status={vmStatus}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">{vmVCPU}</TableCell>
                        <TableCell align="center">{vmMemory}</TableCell>
                        <TableCell align="center">
                          <Box display="flex" gap={0.5} justifyContent="center">
                            <Tooltip title="Power On">
                              <span>
                                <IconButton
                                  size="small"
                                  color="success"
                                  disabled={actionLoading[vm.id]}
                                  onClick={() => handleActionClick(vm, 'power-on')}
                                >
                                  <PlayArrow fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Power Off">
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={actionLoading[vm.id]}
                                  onClick={() => handleActionClick(vm, 'power-off')}
                                >
                                  <Stop fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Restart">
                              <span>
                                <IconButton
                                  size="small"
                                  color="warning"
                                  disabled={actionLoading[vm.id]}
                                  onClick={() => handleActionClick(vm, 'restart')}
                                >
                                  <Autorenew fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Change icon">
                            <IconButton
                              size="small"
                              onClick={(e) => handleIconMenuOpen(e, vm)}
                              sx={{ color: 'rgba(255, 255, 255, 0.5)', '&:hover': { color: 'rgba(255, 255, 255, 0.8)' } }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </StyledTableContainer>
          </Box>
        )}

        {/* Mobile Card View */}
        {!loading && vms.length > 0 && (
          <Box sx={{ display: { xs: 'block', sm: 'none' }, gap: 2 }}>
            {vms.map((vm) => {
              const vmStatus = getVMStatus(vm);
              const vmName = getVMName(vm);
              const vmVCPU = getVMVCPU(vm);
              const vmMemory = getVMMemory(vm);
              const vmIcon = getVMIcon(vm.id, vm.name);
              
              return (
                <Box
                  key={vm.id || vm.name}
                  sx={{
                    background: 'rgba(7, 11, 22, 0.4)',
                    backdropFilter: 'blur(24px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '12px',
                    p: 2,
                    mb: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'rgba(7, 11, 22, 0.6)',
                      border: '1px solid rgba(255, 255, 255, 0.25)',
                    }
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center" gap={1}>
                      {vmIcon ? (
                        <Box
                          component="img"
                          src={vmIcon.path}
                          alt={vmIcon.name}
                          sx={{ width: 20, height: 20, objectFit: 'contain' }}
                        />
                      ) : (
                        <ComputerIcon sx={{ fontSize: 20, color: 'rgba(255, 255, 255, 0.5)' }} />
                      )}
                      <Typography sx={{ color: '#ffffff', fontWeight: 600, fontSize: '0.95rem' }}>
                        {vmName}
                      </Typography>
                    </Box>
                    <StatusChip
                      label={vmStatus}
                      status={vmStatus}
                      size="small"
                    />
                  </Box>
                  
                  <Box display="flex" gap={2} sx={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                    <Box>vCPU: <span style={{ color: '#00d4ff', fontWeight: 600 }}>{vmVCPU}</span></Box>
                    <Box>RAM: <span style={{ color: '#00d4ff', fontWeight: 600 }}>{vmMemory}MB</span></Box>
                  </Box>

                  <Box display="flex" gap={1} justifyContent="flex-end">
                    <Tooltip title="Power On">
                      <IconButton
                        size="small"
                        color="success"
                        disabled={actionLoading[vm.id]}
                        onClick={() => handleActionClick(vm, 'power-on')}
                        sx={{ fontSize: '0.8rem' }}
                      >
                        <PlayArrow fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Power Off">
                      <IconButton
                        size="small"
                        color="error"
                        disabled={actionLoading[vm.id]}
                        onClick={() => handleActionClick(vm, 'power-off')}
                        sx={{ fontSize: '0.8rem' }}
                      >
                        <Stop fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Restart">
                      <IconButton
                        size="small"
                        color="warning"
                        disabled={actionLoading[vm.id]}
                        onClick={() => handleActionClick(vm, 'restart')}
                        sx={{ fontSize: '0.8rem' }}
                      >
                        <Autorenew fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Change icon">
                      <IconButton
                        size="small"
                        onClick={(e) => handleIconMenuOpen(e, vm)}
                        sx={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.5)', '&:hover': { color: 'rgba(255, 255, 255, 0.8)' } }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </CardContent>

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, vm: null, action: null })}
      >
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {getActionText(confirmDialog.action)?.toLowerCase()} the VM "{confirmDialog.vm?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, vm: null, action: null })}>
            Cancel
          </Button>
          <Button onClick={confirmAction} color="primary" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Icon Selection Menu */}
      <Menu
        anchorEl={iconMenuAnchor}
        open={Boolean(iconMenuAnchor)}
        onClose={handleIconMenuClose}
        PaperProps={{
          sx: {
            background: 'rgba(15, 20, 35, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            minWidth: 180,
          }
        }}
      >
        <Typography sx={{ px: 2, py: 1, color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.75rem' }}>
          Select VM Icon
        </Typography>
        {VM_ICONS.map((icon) => (
          <MenuItem
            key={icon.id}
            onClick={() => handleIconSelect(icon.id)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              color: '#ffffff',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <Box
              component="img"
              src={icon.path}
              alt={icon.name}
              sx={{ width: 24, height: 24, objectFit: 'contain' }}
            />
            <Typography>{icon.name}</Typography>
          </MenuItem>
        ))}
        <Typography sx={{ px: 2, py: 0.5, color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.7rem', borderTop: '1px solid rgba(255, 255, 255, 0.1)', mt: 0.5, pt: 1 }}>
          Clear Icon
        </Typography>
        <MenuItem
          onClick={handleIconClear}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            color: '#ff6b6b',
            '&:hover': {
              background: 'rgba(255, 107, 107, 0.1)',
            },
          }}
        >
          <ComputerIcon sx={{ fontSize: 24, color: 'rgba(255, 255, 255, 0.5)' }} />
          <Typography>Default (no icon)</Typography>
        </MenuItem>
      </Menu>
    </StyledCard>
  );
}
